import { usersMenu } from "@/services/users-menus"
import { useQuery } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import { Link, useLocation, useRouter } from "@tanstack/react-router"
import { useStore } from "@tanstack/react-store"
import userStore, { isProfileConfirmed } from "@/stores/user-store"
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "./ui/sidebar"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible"
import { Check, ChevronDown, ChevronRight, Rocket, User } from "lucide-react"
import { Button } from "./ui/button"
import { getLatestVersion } from "@/queries/version"
import semverLt from "semver/functions/lt"
import semverValid from 'semver/functions/valid'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog"
import { registerSW } from 'virtual:pwa-register'
import { useEcho, useEchoPublic } from "@laravel/echo-react"
import {
  getTicketNotificationCount,
  ticketNotificationsCountQueryKey,
} from "@/queries/tickets"

const SidebarDetail = ({isNew = false}: {isNew?: boolean}) => {
  const { user: currentUser } = useStore(userStore)
  const links = usersMenu
  const { setOpenMobile, state, toggleSidebar } = useSidebar()
  const [serverVersion, setServerVersion] = useState<{title: string, version: string, description: string} | null>()
  const [showDetailVersion, setShowDetailVersion] = useState(false)
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [serverUpdatePending, setServerUpdatePending] = useState(true)
  const [justDetail, setJustDetail] = useState(false)
  const router = useRouter()
  const routerLocation = useLocation()

  const updateSW = registerSW({
    onNeedRefresh() {
      // Only auto-reload if it's not a server version update
      if (!serverUpdatePending) {
        updateSW(true)
      }
      // If serverUpdatePending is true, wait for manual update via the sidebar button
    },
    onOfflineReady() {
      console.log('PWA is ready to work offline')
    },
  })

  useEchoPublic('versions', '.VersionUpdated', (payload:any) => {
    setServerVersion(payload)
    setUpdateAvailable(true)
    setJustDetail(false)
  })

  const latestVersion = useQuery({
    queryKey: ['latest', 'version'],
    queryFn: () => getLatestVersion(),
  })

  useEffect(() => {
    if (latestVersion.data) {
      setServerVersion(latestVersion.data.data.data)
      if (semverValid(__APP_VERSION__) && semverValid(latestVersion.data.data.data?.version)) {
        const hasNewVersion = semverLt(__APP_VERSION__, latestVersion.data.data.data?.version || "")
        setUpdateAvailable(hasNewVersion)
        setServerUpdatePending(hasNewVersion)
      } else {
        setUpdateAvailable(false)
        setServerUpdatePending(false)
        updateSW(true)
      }
    } 
  }, [latestVersion.data])

  useEffect(() => {
    const unsubscribe = router.subscribe("onBeforeNavigate", () => {
      setOpenMobile(false)
    })

    return () => unsubscribe()
  }, [router])

  useEffect(() => {
    const latestVersion = localStorage.getItem('latest_version')
    if (latestVersion) {
      const hasNew = semverLt(__APP_VERSION__, latestVersion)
      if (hasNew) {
        localStorage.setItem('latest_version', __APP_VERSION__)
        setShowDetailVersion(true)
        setJustDetail(true)
      }
    } else {
      localStorage.setItem('latest_version', __APP_VERSION__)
    }
    updateSW()
  }, [])

  const notificationCountQuery = useQuery({
    queryKey: ticketNotificationsCountQueryKey,
    queryFn: getTicketNotificationCount,
    // برای این‌که بعد از دریافت هر پیام جدید badge به‌روز بماند
    refetchInterval: 60_000,
  })

  const unreadTicketCount = notificationCountQuery.data ?? 0

  // هنگام دریافت اعلان جدید کاربر (مثلاً پیام جدید تیکت از سمت ادمین/ربات ایساج)
  // تعداد پیام‌های خوانده‌نشده دوباره دریافت می‌شود.
  // رویدادهای group_charge.progress پشت‌سرهم زیاد هستند و ربطی به تیکت ندارند.
  useEcho(
    `user.${currentUser?.id}`,
    ".UserNotification",
    (payload: any) => {
      if (payload?.message?.type !== "group_charge.progress") {
        notificationCountQuery.refetch()
      }
    },
    [currentUser?.id],
  )

  const filteredLinkByAcl = links.filter((d: any) => {
    // آیتم‌هایی که فقط بعد از تایید پروفایل باید دیده شوند
    if (d?.confirmedOnly && !isProfileConfirmed(currentUser)) {
      return
    }

    if (d?.access && currentUser?.type) {
      if (d?.access?.includes(currentUser?.type)) {
        return d
      }
    } else {
      return d
    }
  })

  return (
    <>
      <Dialog open={showDetailVersion} onOpenChange={setShowDetailVersion}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {serverVersion?.title}
            </DialogTitle>
          </DialogHeader>
          <p>شماره نسخه :{serverVersion?.version}</p>
          <p className="font-bold">تغییرات این نسخه : </p>
          <pre className="font-sans-serif whitespace-break-spaces max-h-96 overflow-y-auto">{serverVersion?.description}</pre>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailVersion(false)}>
              بستن 
            </Button>
            {!justDetail && <Button onClick={() => {
              setServerUpdatePending(false)
              updateSW(true)
              location.reload()
              localStorage.setItem('latest_version', serverVersion?.version || '')
            }}>
              <Rocket />
              بروزرسانی
            </Button>}
          </DialogFooter>

        </DialogContent>
      </Dialog>

      <SidebarHeader className="flex flex-row justify-between items-center">
        {state === 'expanded' ?  (isNew ? <div className="flex gap-2 items-center p-4">
          <img src="/s-a-logo@2x.png" alt="esaj-logo" className="w-16" />
          <p className="no-font text-xl font-bold">ایساج</p>
        </div> : <span>ایساج</span>) : <div className="h-12"> </div>}
        <Button size="icon" variant={isNew ? "default" : "ghost"} onClick={toggleSidebar} data-hide={state === 'collapsed'} className={isNew ? "border-2 border-border absolute -left-3 hover:bg-sidebar hover:text-red-600 rounded-full top-5 size-[34px] bg-sidebar text-red-500" : ''}>
        <ChevronRight className={`group-data-[collapsible=icon]:rotate-180 transition-all `}/>
        </Button>
      </SidebarHeader>
      <SidebarContent>

        <SidebarMenu>
      
          {filteredLinkByAcl.map((link: any) =>
            link.children == undefined ? (
              
                      <SidebarMenuItem key={link.title}>
                        <SidebarMenuButton
                          isActive={routerLocation.pathname == link.href}
                          tooltip={{
                            children: <>{link.title}</>,
                            side: 'left',
                          }}
                          asChild
                          className="font-medium data-[active=true]:bg-[#FFEFF0] data-[active=true]:text-primary dark:data-[active=true]:bg-[#FF929E1A]"
                        >
                          <Link to={link.href} target={link.href.startsWith("https") ? '_blank': undefined}>
                            <link.icon className="size-5" />
                            <span>{link.title}</span>
                            {link.href.includes("tickets") && unreadTicketCount > 0 ? (
                      <div className="bg-red-500 rounded-full min-w-5 h-5 px-1 flex justify-center items-center text-white text-[10px] leading-none">
                        {Intl.NumberFormat("fa-ir").format(unreadTicketCount)}
                      </div>
                    ) : (
                      ""
                    )}
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
              
            ) : (
                <Collapsible className="group/collapsible" key={link.title} defaultOpen={
                  link.children.filter((l: any) => routerLocation.pathname === l.href).length > 0
                }>
                  <SidebarGroup>
                          <SidebarGroupLabel asChild className="!px-0" tooltip={link.title}>
                            <CollapsibleTrigger>
                              <link.icon className="size-5 ml-2" />
                              <span>{link.title}</span>
                              <ChevronDown className="mr-auto transition-transform group-data-[state=open]/collapsible:-rotate-180" />
                            </CollapsibleTrigger>
                          </SidebarGroupLabel>
                    <CollapsibleContent className="group-data-[collapsible=icon]:mr-0 mr-4 mt-4">
                      {link.children.map((l: any) => l.access && !l.access.includes(currentUser?.type) ? null :  (
                       
                        <SidebarMenuItem key={l.title}>
                          <SidebarMenuButton
                            isActive={routerLocation.pathname === l.href}
                            tooltip={{
                              children: <>{l.title}</>,
                              side: 'left',
                            }}
                            asChild
                            className="font-medium data-[active=true]:bg-[#FFEFF0] data-[active=true]:text-primary dark:data-[active=true]:bg-[#FF929E1A]"
                          >
                            <Link to={l.href} >
                              <l.icon className="size-5" />
                              <span>{l.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </CollapsibleContent>
                  </SidebarGroup>
                </Collapsible>
            )
          )}
        </SidebarMenu>
      </SidebarContent>

      <SidebarFooter>
      

        
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton className="group-data-[collapsible=icon]:mr-0 flex flex-col h-auto justify-start items-start" tooltip={{
              children: <div className="flex flex-col items-start justify-start">
                <span className="text-lg font-bold ">{currentUser?.name}</span>
              <span>{currentUser?.mobile?.replace("98", "0")}</span>
              </div>,
              side:'left'
            }}>
              <User className="hidden group-data-[collapsible=icon]:block"/>
              <span className="text-lg font-bold group-data-[collapsible=icon]:hidden">{currentUser?.name}</span>
              <span className="group-data-[collapsible=icon]:hidden">{currentUser?.mobile?.replace("98", "0")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
        <SidebarSeparator />
        <SidebarGroup className="group-data-[collapsible=icon]:hidden">
        <SidebarGroupLabel className="justify-between items-center flex">
          <span>
          نسخه فعلی 
          </span>
          <span>
        {__APP_VERSION__}
          </span>
        </SidebarGroupLabel>
        {latestVersion.isFetching ? '' : updateAvailable ? 
        <div className="flex flex-col justify-center">
        
        <div className="flex items-center gap-1 my-2">
            <div className="text-red-200 bg-red-500 rounded-full flex justify-center items-center p-1">
            <Rocket size={18} />
            </div>
            <span className="text-xs">
            یک بروزرسانی جدید وجود دارد
            </span>

        </div>
        <div className="flex gap-1">
        <Button className="w-full" onClick={() => {
          setShowDetailVersion(true)
        }}>بروزرسانی</Button>
        </div>
        </div>
         : <><div className="flex gap-0.5 !text-[10px]">
          <Check className="text-green-500"/>
          آخرین نسخه در حال استفاده است
          </div>
          <Button variant="outline" onClick={() => {
            setShowDetailVersion(true)
            setJustDetail(true)
          }}>
            تغییرات این نسخه
          </Button>
          </>}
        </SidebarGroup>
      </SidebarFooter>
    </>
  )
}

export default SidebarDetail
